import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Empty, Input, Radio, Space, Typography, message, Modal, List } from 'antd';
import { useTranslation } from 'react-i18next';
import './AIPlayGroundLessonPlayer.css';
import { TAIPlayGroundDatasetEntry, TAIPlayGroundLessonConfig } from '../../../models/courses/aiPlayGround';
import { useObjectsApi } from '../../../api/objects/objects';
import type { TImage, TSound } from '../../../models/objects/media';
import { getImagePath, getSoundPath } from '../../../utils/format/valueFormat';
import { useUserApi } from '../../../api/user/userApp';


type LanguageKey = 'fr' | 'en' | 'ar';
type FeatureType = 'text' | 'image' | 'audio';

interface FeatureInput {
  id: string;
  value: string;
  type: FeatureType;
}

interface DetectionResult {
  key: string;
  accuracy: number;
  matched: number;
  total: number;
}

interface AIPlayGroundLessonPlayerProps {
  config: TAIPlayGroundLessonConfig;
  onComplete: () => void;
  statement?: string;
  lessonId?: number;
}

const getLanguageKey = (language: string): LanguageKey => {
  if (language.startsWith('fr')) return 'fr';
  if (language.startsWith('ar')) return 'ar';
  return 'en';
};

const normalizeValue = (raw: string) => raw.replace(/[.,]/g, '').trim().toLowerCase();

const getFeaturesByLanguage = (entry: TAIPlayGroundDatasetEntry, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return entry.features_fr ?? entry.features_en ?? entry.features_ar ?? [];
    case 'ar':
      return entry.features_ar ?? entry.features_en ?? entry.features_fr ?? [];
    default:
      return entry.features_en ?? entry.features_fr ?? entry.features_ar ?? [];
  }
};

const getNoiseByLanguage = (config: TAIPlayGroundLessonConfig, language: LanguageKey): string[] => {
  switch (language) {
    case 'fr':
      return config.bruit_fr ?? config.bruit_en ?? config.bruit_ar ?? [];
    case 'ar':
      return config.bruit_ar ?? config.bruit_en ?? config.bruit_fr ?? [];
    default:
      return config.bruit_en ?? config.bruit_fr ?? config.bruit_ar ?? [];
  }
};

const AIPlayGroundLessonPlayer: React.FC<AIPlayGroundLessonPlayerProps> = ({
  config,
  onComplete,
  statement,
  lessonId,
}) => {
  const { t, i18n } = useTranslation();
  const language = getLanguageKey(i18n.language || 'en');

  const datasetEntries = useMemo(() => Object.entries(config.dataset || {}), [config.dataset]);
  const noiseItems = useMemo(() => getNoiseByLanguage(config, language).map(normalizeValue), [config, language]);

  const [features, setFeatures] = useState<FeatureInput[]>([
    { id: 'feature-0', value: '', type: 'text' },
  ]);
  const [detecting, setDetecting] = useState(false);
  const [result, setResult] = useState<DetectionResult | null>(null);

  // Media picker state (follow admin example)
  const objectsApi = useObjectsApi();
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaPickerType, setMediaPickerType] = useState<Exclude<FeatureType, 'text'>>('image');
  const [mediaLoading, setMediaLoading] = useState(false);
  const [images, setImages] = useState<TImage[]>([]);
  const [sounds, setSounds] = useState<TSound[]>([]);
  const [targetFeatureId, setTargetFeatureId] = useState<string | null>(null);

  const addFeature = () => {
    setFeatures((prev) => [
      ...prev,
      {
        id: `feature-${prev.length}`,
        value: '',
        type: 'text',
      },
    ]);
  };

  const updateFeature = (id: string, value: string) => {
    setFeatures((prev) => prev.map((item) => (item.id === id ? { ...item, value } : item)));
  };

  const updateFeatureType = (id: string, type: FeatureType) => {
    setFeatures((prev) => prev.map((item) => (item.id === id ? { ...item, type, value: type === 'text' ? item.value : '' } : item)));
  };

  const removeFeature = (id: string) => {
    setFeatures((prev) => prev.filter((item) => item.id !== id));
  };

  const openMediaPicker = async (featureId: string, type: Exclude<FeatureType, 'text'>) => {
    setTargetFeatureId(featureId);
    setMediaPickerType(type);
    setMediaPickerOpen(true);
    setMediaLoading(true);
    try {
      if (type === 'image') {
        const resp = await objectsApi.getImages();
        setImages(resp.data || []);
      } else {
        const resp = await objectsApi.getSounds();
        setSounds(resp.data || []);
      }
    } catch (e: any) {
      message.error(e?.response?.data || e?.message || 'Failed to load media');
    } finally {
      setMediaLoading(false);
    }
  };

  const chooseMedia = (path: string) => {
    if (!targetFeatureId) return;
    setFeatures((prev) => prev.map((f) => (f.id === targetFeatureId ? { ...f, value: path } : f)));
    setMediaPickerOpen(false);
    setTargetFeatureId(null);
  };

  // Calculer un hash de config pour invalider un ancien workspace si config a changé
  const configHash = useMemo(() => {
    try {
      const base = JSON.stringify({
        dataset: config?.dataset || {},
        bruit_fr: config?.bruit_fr || [],
        bruit_en: config?.bruit_en || [],
        bruit_ar: config?.bruit_ar || [],
        data_type: config?.data_type || {},
      });
      return base.length;
    } catch {
      return 0;
    }
  }, [config]);

  // Load saved workspace if available
  const { getUserAnswerById, saveUserAnswerById } = useUserApi();

  useEffect(() => {
    if (!lessonId) return;
    getUserAnswerById(lessonId)
      .then((resp: any) => {
        const workspace: string | undefined = resp?.data?.workspace;
        if (!workspace) return;
        try {
          const parsed = JSON.parse(workspace);
          if (!parsed || parsed.configHash !== configHash) {
            return; // configuration changed, skip restore
          }
          if (parsed && Array.isArray(parsed.features)) {
            const restored: FeatureInput[] = parsed.features
              .map((it: any, idx: number) => ({
                id: it.id || `feature-${idx}`,
                type: (it.type === 'image' || it.type === 'audio') ? it.type : 'text',
                value: typeof it.value === 'string' ? it.value : '',
              }));
            if (restored.length) setFeatures(restored);
          }
          if (parsed && parsed.lastResult && typeof parsed.lastResult === 'object') {
            const lr = parsed.lastResult as DetectionResult;
            if (typeof lr.key === 'string' && typeof lr.accuracy === 'number') {
              setResult(lr);
            }
          }
        } catch (_) {
          // ignore invalid workspace
        }
      })
      .catch(() => {/* ignore */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const runDetection = async () => {
    const userFeatures = features
      .map((f) => normalizeValue(f.value))
      .filter((v) => v.length > 0);

    if (!userFeatures.length) {
      message.warning(t('aiPlayGround.student.messages.noFeatures'));
      return;
    }

    if (!datasetEntries.length) {
      message.warning(t('aiPlayGround.student.messages.noDataset'));
      return;
    }

    setDetecting(true);
    try {
      let best: DetectionResult | null = null;

      for (const [keyId, entry] of datasetEntries) {
        const entryFeatures = getFeaturesByLanguage(entry, language).map(normalizeValue);
        const matched = userFeatures.filter((val) => entryFeatures.includes(val)).length;
        const noisePenalty = userFeatures.filter((val) => noiseItems.includes(val)).length;
        const denominator = userFeatures.length + noisePenalty;
        const accuracy = denominator === 0 ? 0 : Math.round((matched / denominator) * 100);

        const current: DetectionResult = {
          key: entry.key || keyId,
          accuracy,
          matched,
          total: userFeatures.length,
        };

        if (!best || current.accuracy > best.accuracy) {
          best = current;
        }
      }

      if (best !== null) {
        setResult(best);
        // Save answer/workspace in all cases (partial or success)
        if (lessonId) {
          try {
            const payload = {
              workspace: JSON.stringify({
                configHash,
                features: features.map((f) => ({ id: f.id, type: f.type, value: f.value })),
                lastResult: best,
              }),
            };
            await saveUserAnswerById(lessonId, payload);
          } catch (_) {
            // ignore save error
          }
        }
        if (best.accuracy === 100) {
          onComplete();
        } else {
          message.info(t('aiPlayGround.student.messages.partial', { accuracy: best.accuracy }));
        }
      }
    } finally {
      setDetecting(false);
    }
  };

  const renderFeatureInputs = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      {features.map((feature, index) => (
        <Card
          key={feature.id}
          size="small"
          title={`${t('aiPlayGround.student.feature')} #${index + 1}`}
          extra={
            features.length > 1 ? (
              <Button type="link" danger size="small" onClick={() => removeFeature(feature.id)}>
                {t('aiPlayGround.student.remove')}
              </Button>
            ) : null
          }
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group
              value={feature.type}
              onChange={(e) => updateFeatureType(feature.id, e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="text">{t('aiPlayGround.student.typeText')}</Radio.Button>
              <Radio.Button value="image">{t('aiPlayGround.student.typeImage')}</Radio.Button>
              <Radio.Button value="audio">{t('aiPlayGround.student.typeAudio')}</Radio.Button>
            </Radio.Group>

            {feature.type === 'text' ? (
              <Input.TextArea
                rows={3}
                value={feature.value}
                placeholder={t('aiPlayGround.student.featurePlaceholder')}
                onChange={(e) => updateFeature(feature.id, e.target.value)}
              />
            ) : (
              <Space direction="vertical">
                {feature.type === 'image' && feature.value ? (
                  <img src={getImagePath(feature.value)} alt="feature" style={{ maxHeight: 120, borderRadius: 6 }} />
                ) : null}
                {feature.type === 'audio' && feature.value ? (
                  <audio src={getSoundPath(feature.value)} controls />
                ) : null}
                <Space>
                  <Button onClick={() => openMediaPicker(feature.id, feature.type as Exclude<FeatureType, 'text'>)}>
                    {feature.type === 'image' ? 'Sélectionner une image' : 'Sélectionner un audio'}
                  </Button>
                  {feature.value && (
                    <Button danger onClick={() => updateFeature(feature.id, '')}>
                      Supprimer
                    </Button>
                  )}
                </Space>
              </Space>
            )}
          </Space>
        </Card>
      ))}
      <Button type="dashed" onClick={addFeature} block>
        {t('aiPlayGround.student.addFeature')}
      </Button>
    </Space>
  );

  // Intentionally no dataset/keys preview for students.

  if (!datasetEntries.length) {
    return <Empty description={t('aiPlayGround.student.empty')} />;
  }

  return (
    <div className="aimb-player">
      {statement || config.statement ? (
        <div className="aimb-statement">{statement || config.statement}</div>
      ) : null}
      <Card className="aimb-section-card">
        <Typography.Paragraph>{t('aiPlayGround.student.instructions')}</Typography.Paragraph>
        {renderFeatureInputs()}
        <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            onClick={runDetection}
            loading={detecting}
            block
          >
            {t('aiPlayGround.student.detect')}
          </Button>
          {result && (
            <Card className="aimb-training-card">
              <Typography.Title level={4}>
                {t('aiPlayGround.student.resultTitle')}
              </Typography.Title>
              <Typography.Paragraph>
                {t('aiPlayGround.student.resultLabel', {
                  key: result.key,
                  accuracy: result.accuracy,
                })}
              </Typography.Paragraph>
              <Typography.Paragraph type={result.accuracy === 100 ? 'success' : 'warning'}>
                {result.accuracy === 100
                  ? t('aiPlayGround.student.perfect')
                  : t('aiPlayGround.student.mixed')}
              </Typography.Paragraph>
            </Card>
          )}
        </Space>
      </Card>

      {/* Dataset/keys preview hidden for students as requested */}

      <Modal
        title={mediaPickerType === 'image' ? 'Sélectionner une image' : 'Sélectionner un audio'}
        open={mediaPickerOpen}
        onCancel={() => setMediaPickerOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        {mediaLoading ? (
          <Typography.Text>Chargement...</Typography.Text>
        ) : mediaPickerType === 'image' ? (
          <List
            dataSource={images}
            rowKey={(item) => String(item.id)}
            renderItem={(item) => (
              <List.Item onClick={() => chooseMedia(item.file_path)} style={{ cursor: 'pointer' }}>
                <Space>
                  <img src={item.file_path} alt={item.file_name} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                  <span>{item.file_name}</span>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <List
            dataSource={sounds}
            rowKey={(item) => String(item.id)}
            renderItem={(item) => (
              <List.Item onClick={() => chooseMedia(item.file_path)} style={{ cursor: 'pointer' }}>
                <Space>
                  <span>{item.file_name}</span>
                </Space>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default AIPlayGroundLessonPlayer;
